import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { name: "", customer_id: "", contact_name: "", email: "", phone: "", address: "", rates: [] };

export default function Clients() {
  const { currentId } = useCompany();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => {
    if (currentId) load();
  }, [currentId]);

  const openNew = () => {
    setForm(EMPTY);
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (c) => {
    setForm({ name: c.name, customer_id: c.customer_id || "", contact_name: c.contact_name || "", email: c.email || "", phone: c.phone || "", address: c.address || "", rates: (c.rates || []).map((r) => ({ name: r.name || "", rate: r.rate != null ? String(r.rate) : "" })) });
    setEditId(c.id);
    setOpen(true);
  };

  const addRate = () => setForm((f) => ({ ...f, rates: [...f.rates, { name: "", rate: "" }] }));
  const setRate = (i, key, val) => setForm((f) => {
    const rates = [...f.rates];
    rates[i] = { ...rates[i], [key]: val };
    return { ...f, rates };
  });
  const removeRate = (i) => setForm((f) => ({ ...f, rates: f.rates.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    const payload = {
      ...form,
      rates: form.rates
        .filter((r) => r.name.trim())
        .map((r) => ({ name: r.name.trim(), rate: parseFloat(r.rate) || 0 })),
    };
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Client updated" : "Client added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save client");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Clients</h1>
          <p className="text-slate-500 text-sm mt-1">People and businesses you invoice.</p>
        </div>
        <Button data-testid="add-client-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" /> New Client
        </Button>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {clients.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No clients yet. Add your first client.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Phone</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.phone || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="icon" data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`delete-client-${c.id}`} onClick={() => remove(c.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Client" : "New Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Company / Client Name</Label>
              <Input
                data-testid="client-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-md"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer ID</Label>
                <Input
                  data-testid="client-customer-id-input"
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                  className="mt-1 rounded-md"
                  placeholder="RARE-01"
                />
              </div>
              <div>
                <Label>Contact Name</Label>
                <Input
                  data-testid="client-contact-name-input"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="mt-1 rounded-md"
                  placeholder="Mike Rideout"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="client-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 rounded-md"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  data-testid="client-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 rounded-md"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                data-testid="client-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-1 rounded-md"
              />
            </div>
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label>People &amp; Rates</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Used to log time on this client's jobs.</p>
                </div>
                <Button variant="outline" size="sm" data-testid="add-rate-btn" onClick={addRate} className="rounded-md">
                  <Plus className="h-4 w-4 mr-1" /> Add person
                </Button>
              </div>
              {form.rates.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">No people added yet.</p>
              ) : (
                <div className="space-y-2">
                  {form.rates.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        data-testid={`rate-name-${i}`}
                        value={r.name}
                        onChange={(e) => setRate(i, "name", e.target.value)}
                        className="rounded-md flex-1"
                        placeholder="Name (e.g. Iain Munro)"
                      />
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          data-testid={`rate-value-${i}`}
                          value={r.rate}
                          onChange={(e) => setRate(i, "rate", e.target.value)}
                          className="rounded-md pl-6 text-right tabular-nums"
                          placeholder="75"
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-8">/hr</span>
                      <Button variant="ghost" size="icon" data-testid={`remove-rate-${i}`} onClick={() => removeRate(i)}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">
              Cancel
            </Button>
            <Button data-testid="save-client-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
